import ProductDesc from "@/components/Desc/ProductDesc";
import { OrderAddItemV2, OrderAddV2, OrderV2, WLocation, AddItemLocation, OrderItemV2 } from "@/types/inventoryV2";
import { Form, InputNumber, Modal, Typography, Button, Tooltip, Divider, App, Flex, Select, Tag, notification, Steps, Spin, Result, Space, Descriptions } from "antd"
import { useEffect, useState, Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ChangeEnum, OperateEnum, OperateEnum2, ConfirmedEnum } from "@/types/inventory";
import { DeleteOutlined, PlusOutlined, BarcodeOutlined, CheckCircleOutlined, ExclamationCircleTwoTone } from '@ant-design/icons';
import { LocationSelect } from "@/components/Selector";
import SText from "@/styles/SText";
import BarcodeEditModal from "@/pages/Warehouse/pages/Inventory/components/InboundOrderForm/BarcodeEditModal";
import { getLocationIdx } from "@/utils/wLocation";
import { orderAdd, orderConfirm, orderDel } from "@/apis/inventoryv2";
import OrderDetailV2 from "@/pages/Warehouse/pages/Order/components/OrderDetailV2";
import OutboundOrderForm from "../../pages/Inventory/components/OutboundOrderForm";
import { DescriptionsItemType } from "antd/es/descriptions";
import { SourceTypeEnum } from "@/types/common";
import { itemUpdateByKey } from "@/apis/purchase";
import { PurchaseOrderItem, PurchaseOrderStateEnum } from "@/types/purchase";
import useSourceType from "@/hooks/useSourceType";
import OrderConfirmInspection from "@/components/OrderConfirmInspection";
import { SpecialHandlingConfirm } from "@/types/inventoryV2";



interface ReturnModalFormProps {
  detail: OrderV2;
  onCancel: () => void;
  onSuccess?: (order: OrderV2, type?: "ADD" | "UPDATE" | "DEL") => void;
}

// 入库单退库时的库位信息（只能从原有库位选择）
interface OutboundLocation {
  location: WLocation;
  originalCount: number; // 原入库数量
  availableCount: number; // 可退库数量
  returnCount: number; // 退库数量
  originalBarcodes: string[]; // 原条码
  selectedBarcodes: string[]; // 选中的条码
}

// 出库单退库时的库位信息（可以选择新库位）
interface InboundLocation {
  location: WLocation;
  count: number;
  barcodes: string[];
}

interface ReturnPurchaseFinishItem {
  id: string;
  orderNumber: string;
  source: PurchaseOrderItem;
}

const ReturnModalForm: React.FC<ReturnModalFormProps> = ({ detail, onCancel, onSuccess }) => {

  const [form] = Form.useForm<OrderAddV2>();
  const { t } = useTranslation();
  const { opTypes, changeTypes, getOpType, getChangeType } = useSourceType();
  // 判断是入库单退库还是出库单退库
  const isInboundReturn = detail?.type && [ChangeEnum.IN, ChangeEnum.PRE_IN].includes(detail.type);
  const isOutboundReturn = detail?.type && [ChangeEnum.OUT, ChangeEnum.PRE_OUT].includes(detail.type);
  const opType = getOpType(isInboundReturn ? OperateEnum.IN_RETURN : isOutboundReturn ? OperateEnum.OUT_RETURN : null);
  const orderType = getChangeType(isInboundReturn ? ChangeEnum.OUT : isOutboundReturn ? ChangeEnum.IN : null);

  // 用于管理每个产品的库位信息
  const [itemLocations, setItemLocations] = useState<Map<string, OutboundLocation[] | InboundLocation[]>>(new Map());

  // 用于管理当前正在编辑的产品和库位
  const [currentEditingItem, setCurrentEditingItem] = useState<{ itemId: string | null; locationId?: string | null }>({ itemId: null });
  const [changingLocation, setChangingLocation] = useState<{ itemId: string | null; locationId: string | null }>({ itemId: null, locationId: null });

  // 退货原因按商品独立保存，避免多个商品的下拉框联动。
  const [itemReturnReasons, setItemReturnReasons] = useState<Map<string, OperateEnum2>>(new Map());
  const [isReturning, setIsReturning] = useState<boolean>(false);
  const [current, setCurrent] = useState<number>(0);
  const [orderResult, setOrderResult] = useState<OrderV2 | null>(null);
  const [openCon, setOpenCon] = useState(false);
  const [openDel, setOpenDel] = useState(false);
  const [openInspection, setOpenInspection] = useState(false);
  const [inspectionItems, setInspectionItems] = useState<OrderItemV2[]>([]);
  const [selectedOrderState, setSelectedOrderState] = useState<
    PurchaseOrderStateEnum | undefined
  >(undefined);

  // 条码编辑弹窗
  const [barcodeModalInfo, setBarcodeModalInfo] = useState<{
    open: boolean;
    itemId: string | null;
    locationId: string | null;
    barcodes: string[];
    originBarcodes?: string[];
    maxCount?: number;
  }>({ open: false, itemId: null, locationId: null, barcodes: [] });

  useEffect(() => {
    if (!detail) return;

    const locationsMap = new Map<string, OutboundLocation[] | InboundLocation[]>();

    if (isInboundReturn) {
      // 入库单退库：初始化原有库位信息
      detail.items.forEach(item => {
        const locations: OutboundLocation[] = item.locations.map(loc => ({
          location: loc.location,
          originalCount: loc.count,
          availableCount: loc.count, // 实际应根据当前库存计算
          returnCount: 0,
          originalBarcodes: loc.barcodes?.map(b => b.barcode) || [],
          selectedBarcodes: []
        }));
        locationsMap.set(item.id, locations);
      });
    } else if (isOutboundReturn) {
      // 出库单退库：基于原单据的库位信息初始化
      detail.items.forEach(item => {
        const locations: InboundLocation[] = item.locations.map(loc => ({
          location: loc.location,
          count: 0,
          barcodes: [] // 条码初始化为空数组，后续可编辑
        }));
        locationsMap.set(item.id, locations);
      });
    }

    setItemLocations(locationsMap);
    setItemReturnReasons(new Map());

    // 设置表单初始值
    form.setFieldsValue({
      items: detail.items.map((item) => ({
        sourceType: item.sourceType,
        sourceId: item.sourceId,
        productId: item.productId,
        count: 0, // 退库数量初始为0
        lot: item.lot,
        thdlot: item.thdLot,
        desc: item.desc,
        locations: []
      })),
    });
  }, [detail, isInboundReturn, isOutboundReturn])

  const isInReturn = useMemo(() => {
    return [ChangeEnum.IN, ChangeEnum.ORDER_IN, ChangeEnum.PRE_IN].includes(detail.type)
  }, [detail.type])

  const purchaseItemsNeedFinishUpdate = useMemo<ReturnPurchaseFinishItem[]>(() => {
    const matchedItems = detail.items
      .filter(
        (item): item is typeof item & {
          sourceType: SourceTypeEnum.PURCHASE_ITEM;
          source: PurchaseOrderItem;
        } =>
          item.sourceType === SourceTypeEnum.PURCHASE_ITEM &&
          !!item.source &&
          (item.source.orderState === PurchaseOrderStateEnum.COMPLETED || item.source.orderState === PurchaseOrderStateEnum.DONE)
      )
      .map(item => ({
        id: item.source.id,
        orderNumber: ((item.source.order as { number?: string } | null)?.number) || item.source.number,
        source: item.source
      }));

    const uniqueItems = new Map<string, ReturnPurchaseFinishItem>();
    matchedItems.forEach(item => uniqueItems.set(item.id, item));
    return Array.from(uniqueItems.values());
  }, [detail.items]);

  // 是否需要采购单完工状态步骤
  const hasPurchaseFinishStep = !!isInboundReturn && purchaseItemsNeedFinishUpdate.length > 0;
  const finishStatusStepIndex = hasPurchaseFinishStep ? 2 : -1;
  const completeStepIndex = hasPurchaseFinishStep ? 3 : 2;

  useEffect(() => {
    if (!hasPurchaseFinishStep) {
      setSelectedOrderState(undefined);
    }
  }, [hasPurchaseFinishStep]);

  const renderPurchaseFinishStateTag = (orderState?: PurchaseOrderStateEnum) => {
    if (orderState === PurchaseOrderStateEnum.COMPLETED) {
      return <Tag color="purple">{t("purchase.completedProduction")}</Tag>;
    } else if (orderState === PurchaseOrderStateEnum.DONE) {
      return <Tag color="green">{t("layout.purDone")}</Tag>;
    }
    return "-";
  };

  // 处理入库单退库的库位数量变化
  const handleOutboundLocationCountChange = (itemId: string, locationId: string, count: number) => {
    setItemLocations(prev => {
      const newMap = new Map(prev);
      const locations = newMap.get(itemId) as OutboundLocation[];
      if (locations) {
        const updatedLocations = locations.map(loc => {
          if (loc.location.id === locationId) {
            return { ...loc, returnCount: count };
          }
          return loc;
        });
        newMap.set(itemId, updatedLocations);
      }
      return newMap;
    });
  };

  // 处理出库单退库的库位数量变化
  const handleInboundLocationCountChange = (itemId: string, locationId: string, count: number) => {
    setItemLocations(prev => {
      const newMap = new Map(prev);
      const locations = newMap.get(itemId) as InboundLocation[];
      if (locations) {
        const updatedLocations = locations.map(loc => {
          if (loc.location.id === locationId) {
            return { ...loc, count };
          }
          return loc;
        });
        newMap.set(itemId, updatedLocations);
      }
      return newMap;
    });
  };

  // 处理库位选择
  const handleLocationSelectChange = (location: WLocation) => {
    if (isOutboundReturn) {
      // 处理更改现有库位的情况
      if (changingLocation.itemId && changingLocation.locationId) {
        const itemId = changingLocation.itemId;
        const locations = itemLocations.get(itemId) as InboundLocation[] || [];

        // 检查新库位是否已存在（排除当前正在更改的库位）
        if (locations.some(loc => loc.location.id === location.id && loc.location.id !== changingLocation.locationId)) {
          notification.warning({ placement: 'top', message: t('warehouse.locationExist') });
          return;
        }

        // 更新库位
        const updatedLocations = locations.map(loc => {
          if (loc.location.id === changingLocation.locationId) {
            return { ...loc, location };
          }
          return loc;
        });

        setItemLocations(prev => {
          const newMap = new Map(prev);
          newMap.set(itemId, updatedLocations);
          return newMap;
        });
        setChangingLocation({ itemId: null, locationId: null });
      }
      // 处理添加新库位的情况
      else if (currentEditingItem.itemId) {
        const itemId = currentEditingItem.itemId;
        const locations = itemLocations.get(itemId) as InboundLocation[] || [];

        // 检查库位是否已存在
        if (locations.some(loc => loc.location.id === location.id)) {
          notification.warning({ placement: 'top', message: t('warehouse.locationExist') });
          return;
        }

        const newLocation: InboundLocation = {
          location,
          count: 0,
          barcodes: []
        };

        setItemLocations(prev => {
          const newMap = new Map(prev);
          newMap.set(itemId, [...locations, newLocation]);
          return newMap;
        });
        setCurrentEditingItem({ itemId: null });
      }
    }
  };

  // 处理删除库位
  const handleDeleteLocation = (itemId: string, locationId: string) => {
    setItemLocations(prev => {
      const newMap = new Map(prev);
      const locations = newMap.get(itemId) || [];
      if (isOutboundReturn) {
        newMap.set(itemId, (locations as InboundLocation[]).filter(loc => loc.location.id !== locationId));
      } else {
        newMap.set(itemId, (locations as OutboundLocation[]).filter(loc => loc.location.id !== locationId));
      }
      return newMap;
    });
  };

  // 处理条码编辑
  const handleBarcodeEdit = (barcodes: string[]) => {
    const { itemId, locationId } = barcodeModalInfo;
    if (!itemId || !locationId) return;

    if (isOutboundReturn) {
      // 出库单退库：更新条码
      setItemLocations(prev => {
        const newMap = new Map(prev);
        const locations = newMap.get(itemId) as InboundLocation[];
        if (locations) {
          const updatedLocations = locations.map(loc => {
            if (loc.location.id === locationId) {
              return { ...loc, barcodes: barcodes.slice(0, loc.count) };
            }
            return loc;
          });
          newMap.set(itemId, updatedLocations);
        }
        return newMap;
      });
    } else if (isInboundReturn) {
      // 入库单退库：更新选中的条码
      setItemLocations(prev => {
        const newMap = new Map(prev);
        const locations = newMap.get(itemId) as OutboundLocation[];
        if (locations) {
          const updatedLocations = locations.map(loc => {
            if (loc.location.id === locationId) {
              return { ...loc, selectedBarcodes: barcodes.slice(0, loc.returnCount) };
            }
            return loc;
          });
          newMap.set(itemId, updatedLocations);
        }
        return newMap;
      });
    }

    setBarcodeModalInfo({ open: false, itemId: null, locationId: null, barcodes: [] });
  };

  const handleData = (values: OrderAddV2) => {
    // 根据itemLocations构造locations数据
    const items: OrderAddItemV2[] = values.items.map((item, index) => {
      const itemId = detail.items[index].id;
      const locations = itemLocations.get(itemId) || [];

      let itemLocationsList: AddItemLocation[] = [];
      let totalCount = 0;

      if (isInboundReturn) {
        // 入库单退库：只包含有退库数量的库位
        const outLocations = locations as OutboundLocation[];
        itemLocationsList = outLocations
          .filter(loc => loc.returnCount > 0)
          .map(loc => ({
            locationId: loc.location.id,
            count: loc.returnCount,
            barcodes: loc.selectedBarcodes
          }));
        totalCount = outLocations.reduce((sum, loc) => sum + loc.returnCount, 0);
      } else if (isOutboundReturn) {
        // 出库单退库：包含所有添加的库位
        const inLocations = locations as InboundLocation[];
        itemLocationsList = inLocations.map(loc => ({
          locationId: loc.location.id,
          count: loc.count,
          barcodes: loc.barcodes
        }));
        totalCount = inLocations.reduce((sum, loc) => sum + loc.count, 0);
      }

      return {
        ...item,
        opType2: itemReturnReasons.get(itemId),
        count: totalCount,
        locations: itemLocationsList
      };
    });

    addOrder(items);
  };

  const addOrder = async (items: OrderAddItemV2[]) => {
    const itemsReturn = items.filter(item => item.count > 0);
    if (itemsReturn.length === 0) return notification.error({ placement: 'top', message: t('product.noReturnQuantity') });

    // 兼容订单级 opType2：只有所有退货商品原因一致时才回填整单原因。
    const firstOpType2 = itemsReturn[0]?.opType2;
    const orderOpType2 = itemsReturn.every(item => item.opType2 === firstOpType2) ? firstOpType2 : undefined;

    setIsReturning(true);
    let order: OrderV2 | null = null;
    try {
      order = await orderAdd({
        type: isInReturn ? ChangeEnum.OUT : ChangeEnum.IN,
        opType: isInReturn ? OperateEnum.IN_RETURN : OperateEnum.OUT_RETURN,    //涉及到数量计算，禁止改动，入库单退库就是IN_RETURN，出库单退库就是OUT_RETURN
        opType2: orderOpType2,
        returnOrderId: detail.id,
        desc: detail.desc,
        preTime: detail.preTime,
        arrivalAt: detail.arrivalAt,
        sourceId: detail.sourceId ?? undefined,
        sourceType: detail.sourceType ?? undefined,
        items: itemsReturn,
      })

      if (order) {
        setOrderResult(order);
        setCurrent(1);
      }
    } catch (error: any) {
      if (error?.code === 73007 || error?.code === 73008) {
        if (Array.isArray(error?.extra)) {
          let errCodes = detail?.items
            ?.filter(item => error.extra.includes(item.productId) || error.extra.includes(item.product?.code))
            ?.map(item => item.product?.code ?? '')
            ?.join(', ')
          notification.error({
            placement: 'top',
            message: t('apiError.error31'),
            description: errCodes,
          });
        } else {
          notification.error({
            placement: 'top',
            message: t('warehouse.outboundError'),
            description: error + '',
          });
        }
      } else notification.error({
        placement: 'top',
        message: t('warehouse.outboundError'),
        description: error + '',
      });
    }
    setIsReturning(false);
  };

  // 第一步：确认弹窗点击确定后，打开巡检弹窗
  const handleConfirmModalOk = () => {
    setOpenCon(false);
    setInspectionItems(orderResult?.items || []);
    setOpenInspection(true);
  };

  const confirmOrder = async (specialHandling: SpecialHandlingConfirm[]) => {
    setIsReturning(true);
    try {
      if (hasPurchaseFinishStep && selectedOrderState !== undefined) {
        for (const item of purchaseItemsNeedFinishUpdate) {
          await itemUpdateByKey(item.id, { key: 'orderState', value: selectedOrderState });
        }
      }
      await orderConfirm(orderResult?.id ?? '', 0, specialHandling);
      // try {
      //   await orderConfirm(orderResult?.id ?? '', 0, specialHandling);
      // } catch (error: any) {
      //   if (error?.code === 70845) await orderConfirm(orderResult?.id ?? '', 0,);
      //   else throw error
      // }
      notification.success({ placement: 'top', message: t('warehouse.returnStockSuccess') })
      setIsReturning(false);
      setCurrent(completeStepIndex);
    } catch (error: any) {
      if (error?.code === 73011) {
        let ids: string[] = (error?.extra ?? []);
        let codes = (orderResult?.items ?? []).filter(item => ids.includes(item.productId)).map(p => p.product.code).join(', ')
        notification.error({
          message: t('warehouse.barcodeErr'),
          description: codes
        });
        // notification.error({
        //   placement: 'top',
        //   message: t('warehouse.returnStockFail'),
        //   description: t('warehouse.inventoryMismatch')
        // })
      } else if (error === 72811) {
        notification.error({
          placement: 'top',
          message: t('warehouse.returnStockFail'),
          description: t('product.dataError')
        });
      } else if (error?.code === 73007 || error?.code === 73008) {
        if (Array.isArray(error?.extra)) {
          let errCodes = detail?.items
            ?.filter(item => error.extra.includes(item.productId) || error.extra.includes(item.product?.code))
            ?.map(item => item.product?.code ?? '')
            ?.join(', ')
          notification.error({
            placement: 'top',
            message: t('apiError.error31'),
            description: errCodes,
          });
        } else notification.error({ placement: 'top', message: t('warehouse.returnStockFail'), description: t('apiError.error31') });
      } else if (error === 72904) {
        notification.error({ placement: 'top', message: t('warehouse.returnStockFail'), description: t('warehouse.barcodeErr') });
      } else if (error === 73010) {
        notification.error({
          placement: 'top',
          message: t('common.operationFailed'),
          description: t('warehouse.locFailed'),
        });
      } else if (error?.code === 73024) {
        notification.error({
          placement: 'top',
          message: t('common.operationFailed'),
          description: t('warehouse.locProNot', { locIdx: error?.extra?.locationIndex ?? '', proCode: error?.extra?.productCode ?? '' }),
        });
      } else if ([73028].includes(error?.code)) {
        notification.error({
          message: t('warehouse.insufficientInventory'),
          description: error?.extra?.productCode ?? ''
        })
      } else notification.error({ placement: 'top', message: t('warehouse.returnStockFail'), description: t('requirement.validError') })
    }
    setIsReturning(false);
  };

  const handleNext = async () => {
    if (current === 0) {
      if (!validateData()) return;
      form.submit();
    } else if (current === 1) {
      if (hasPurchaseFinishStep) {
        // 有采购单：进入完工状态选择步骤
        setCurrent(finishStatusStepIndex);
      } else {
        // 无采购单：直接弹出确认弹窗
        setOpenCon(true);
      }
    } else if (current === finishStatusStepIndex) {
      // 采购单完工状态步骤：校验选择后弹出确认弹窗
      if (selectedOrderState === undefined) {
        notification.error({
          placement: 'top',
          message: t('common.tip'),
          description: `${t('common.plSelect')}${t('purchase.deliveryStatus')}`
        });
        return;
      }
      setOpenCon(true);
    } else if (current === completeStepIndex) {
      onCancel();
      onSuccess?.({ ...orderResult!, confirmState: ConfirmedEnum.CONFIRM, confirmedAt: Date.now() }, "ADD");
    }
  };

  const handleClose = () => {
    if (current !== completeStepIndex && orderResult) setOpenDel(true);
    else onCancel();
  };

  const handleDelClose = async () => {
    setOpenDel(false);
    try {
      setIsReturning(true);
      await orderDel(orderResult?.id ?? "");
      notification.success({
        message: t('common.deleteSuccess'),
        description: orderResult?.number
      })
      onCancel();
    } catch (error) {
      notification.error({
        message: t('order.optErr')
      })
    } finally {
      setIsReturning(false);
    }
  };

  // 验证数据
  const validateData = (): boolean => {
    let hasError = false;
    const messages: string[] = [];

    // 用于检查条码重复的Map: barcode -> {productName, locationCode}[]
    const barcodeLocationMap = new Map<string, Array<{ productName: string, locationCode: string }>>();

    detail.items.forEach((item) => {
      const itemId = item.id;
      const locations = itemLocations.get(itemId) || [];
      const productName = item.product?.name || '';

      if (isInboundReturn) {
        const outLocations = locations as OutboundLocation[];
        const totalReturn = outLocations.reduce((sum, loc) => sum + loc.returnCount, 0);

        if (totalReturn === 0) {
          return; // 跳过没有退库数量的产品
        }

        // 检查每个库位的退库数量不超过可用数量
        outLocations.forEach(loc => {
          if (loc.returnCount > loc.availableCount) {
            hasError = true;
            messages.push(
              `${productName}: ${getLocationIdx(loc.location)} ${t('warehouse.returnExceedsAvailable')}`
            );
          }

          // 收集条码信息用于重复检查
          if (loc.selectedBarcodes && loc.selectedBarcodes.length > 0) {
            const locationCode = getLocationIdx(loc.location);
            loc.selectedBarcodes.forEach(barcode => {
              if (!barcodeLocationMap.has(barcode)) {
                barcodeLocationMap.set(barcode, []);
              }
              barcodeLocationMap.get(barcode)!.push({ productName, locationCode });
            });
          }
        });
      } else if (isOutboundReturn) {
        const inLocations = locations as InboundLocation[];
        const totalReturn = inLocations.reduce((sum, loc) => sum + loc.count, 0);

        if (totalReturn === 0) {
          return; // 跳过没有退库数量的产品
        }

        // 检查总退库数量不超过原出库数量
        if (totalReturn > item.count) {
          hasError = true;
          messages.push(
            `${productName}: ${t('warehouse.returnExceedsOriginal', { original: item.count })}`
          );
        }

        // 收集条码信息用于重复检查
        inLocations.forEach(loc => {
          if (loc.barcodes && loc.barcodes.length > 0) {
            const locationCode = getLocationIdx(loc.location);
            loc.barcodes.forEach(barcode => {
              if (!barcodeLocationMap.has(barcode)) {
                barcodeLocationMap.set(barcode, []);
              }
              barcodeLocationMap.get(barcode)!.push({ productName, locationCode });
            });
          }
        });
      }
    });

    // 检查条码重复
    barcodeLocationMap.forEach((locations, barcode) => {
      if (locations.length > 1) {
        hasError = true;
        const locationCodes = locations.map(loc => loc.locationCode).join('和');
        messages.push(
          t("warehouse.barcodeDuplicateInLocations", { barcode, locationCodes })
        );
      }
    });

    if (hasError) {
      notification.error({
        placement: 'top',
        message: t('warehouse.validationError'),
        description: (
          <ul style={{ marginTop: 8, paddingLeft: 20 }}>
            {messages.map((msg, index) => (
              <li key={index}>{msg}</li>
            ))}
          </ul>
        ),
      });
      return false;
    }

    return true;
  };

  // Form内容作为第一步
  const formContent = (
    <div style={{ width: "100%", height: "100%", overflow: "auto" }}>
      <Form<OrderAddV2> form={form} onFinish={handleData}>
        <Form.List name={"items"}>
          {(fields) => (
            fields.map((field, index) => {
              const item = detail?.items[index];
              if (!item) return null;

              const locations = itemLocations.get(item.id) || [];
              const totalReturnCount = isInboundReturn
                ? (locations as OutboundLocation[]).reduce((sum, loc) => sum + loc.returnCount, 0)
                : (locations as InboundLocation[]).reduce((sum, loc) => sum + loc.count, 0);

              return (
                <div key={field.key} style={{ marginBottom: 24 }}>
                  <Typography.Title level={5}>{t('purchase.productInfo')}</Typography.Title>
                  <ProductDesc
                    product={item.product || null}
                    showtype="SUPPLIER"
                    otherItems={[
                      { key: "quantity", label: t('warehouse.productNum'), children: item.count },
                      isInReturn ? ({} as DescriptionsItemType) : { key: "lot", label: t('warehouse.batchNo'), children: item.lot || '-', },
                      { key: "thdLot", label: t('warehouse.supplierBatchNo'), children: item.thdLot || '-' },
                    ]}
                  />

                  <Flex style={{ marginTop: 16, marginBottom: 8 }} justify="space-between">
                    <Typography.Text strong>{t('warehouse.returnQuantity')}: {totalReturnCount}</Typography.Text>
                    {isInReturn && <Typography.Text strong>{t('warehouse.returnLot')}: {item.lot || '-'}</Typography.Text>}
                    <Flex gap={8} align="center">
                      <Typography.Text strong>{t('warehouse.returnRn')}</Typography.Text>
                      <Select style={{ minWidth: 120 }}
                        value={itemReturnReasons.get(item.id)} placeholder={t('scan.selectPl')}
                        onChange={(value) => {
                          setItemReturnReasons(prev => {
                            const newMap = new Map(prev);
                            newMap.set(item.id, value);
                            return newMap;
                          });
                        }}
                        popupMatchSelectWidth={false}
                        labelRender={(label) => label.label}
                      >
                        <Select.Option key={1} value={OperateEnum2.SURPLUS} >{t('warehouse.surplus')}</Select.Option>
                        <Select.Option key={2} value={OperateEnum2.DEFECTIVE} >{t('warehouse.defective')}</Select.Option>
                        <Select.Option key={3} value={OperateEnum2.REFURBISHED} >{t('warehouse.refurbished')}</Select.Option>
                        <Select.Option key={3} value={OperateEnum2.REOUTRECVD} >{t('warehouse.reoutrecvdpicking')}</Select.Option>
                      </Select>
                    </Flex>
                  </Flex>

                  {/* 入库单退库：显示原有库位 */}
                  {isInboundReturn && (
                    <div style={{ marginTop: 12 }}>
                      <Typography.Text>{t('warehouse.selectLocationToReturn')}:</Typography.Text>
                      {(locations as OutboundLocation[]).map((loc) => (
                        <div key={`${item.id}-${loc.location.id}`} style={{ marginTop: 8, padding: '8px 12px', border: '1px solid #f0f0f0', borderRadius: 4 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <SText text={getLocationIdx(loc.location)} style={{ flex: 1 }} />
                            <span>{t('warehouse.available')}: {loc.availableCount}</span>
                            <InputNumber changeOnWheel
                              value={loc.returnCount}
                              min={0}
                              max={loc.availableCount}
                              onChange={(value) => handleOutboundLocationCountChange(item.id, loc.location.id, value || 0)}
                              style={{ width: 100 }}
                            />
                            <Tooltip title={t('warehouse.editBarcodes')}>
                              <Button
                                disabled={loc.returnCount === 0}
                                type="text"
                                icon={<BarcodeOutlined />}
                                size="small"
                                onClick={() => {
                                  setBarcodeModalInfo({
                                    open: true,
                                    itemId: item.id,
                                    locationId: loc.location.id,
                                    barcodes: loc.selectedBarcodes || [],
                                    originBarcodes: loc.originalBarcodes,
                                    maxCount: loc.returnCount
                                  });
                                }}
                              />
                            </Tooltip>
                            <Tooltip title={t('warehouse.barcodeCount')}>
                              <span>{loc.selectedBarcodes.length}</span>
                            </Tooltip>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* 出库单退库：可以选择新库位 */}
                  {isOutboundReturn && (
                    <div style={{ marginTop: 12 }}>
                      <Typography.Text>{t('warehouse.selectLocationToInbound')}:</Typography.Text>
                      <div style={{ marginTop: 8 }}>
                        {(locations as InboundLocation[]).map((loc) => (
                          <div key={`${item.id}-${loc.location.id}`} style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                            <InputNumber changeOnWheel
                              addonBefore={
                                <Tooltip title={t('warehouse.changeLocation')}>
                                  <div
                                    style={{
                                      cursor: 'pointer',
                                      userSelect: 'none',
                                      display: 'inline-block',
                                      width: '360px',
                                      wordBreak: "break-all",
                                      whiteSpace: "normal"
                                    }}
                                    onClick={() => {
                                      setChangingLocation({
                                        itemId: item.id,
                                        locationId: loc.location.id
                                      });
                                    }}
                                  >
                                    {getLocationIdx(loc.location)}
                                  </div>
                                </Tooltip>
                              }
                              addonAfter={
                                <Tooltip title={t('warehouse.barcodeCount')}>
                                  <span>{loc.barcodes.length}</span>
                                </Tooltip>
                              }
                              controls={false}
                              value={loc.count}
                              min={0}
                              max={item.count - ((locations as InboundLocation[]).filter(l => l.location.id !== loc.location.id).reduce((sum, l) => sum + l.count, 0))}
                              style={{ flex: 1 }}
                              onChange={(value) => handleInboundLocationCountChange(item.id, loc.location.id, value || 0)}
                            />
                            <Tooltip title={t('warehouse.editBarcodes')}>
                              <Button disabled={loc.count === 0}
                                type="text"
                                icon={<BarcodeOutlined />}
                                size="small"
                                onClick={() => {
                                  // 收集该item所有位置的所有条码作为originBarcodes
                                  const allBarcodes: string[] = [];
                                  item.locations.forEach(itemLoc => {
                                    itemLoc.barcodes?.forEach(barcode => {
                                      allBarcodes.push(barcode.barcode);
                                    });
                                  });

                                  setBarcodeModalInfo({
                                    open: true,
                                    itemId: item.id,
                                    locationId: loc.location.id,
                                    barcodes: loc.barcodes || [],
                                    originBarcodes: allBarcodes,
                                    maxCount: loc.count
                                  });
                                }}
                              />
                            </Tooltip>
                            <Tooltip title={t('common.del')}>
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                size="small"
                                onClick={() => handleDeleteLocation(item.id, loc.location.id)}
                              />
                            </Tooltip>
                          </div>
                        ))}
                        <Tooltip title={t('warehouse.addLocation')}>
                          <Button
                            type="dashed"
                            icon={<PlusOutlined />}
                            size="small"
                            style={{ width: '100%', marginTop: 8 }}
                            disabled={totalReturnCount >= item.count}
                            onClick={() => setCurrentEditingItem({ itemId: item.id })}
                          >
                            {t('common.add')}
                          </Button>
                        </Tooltip>
                      </div>
                    </div>
                  )}
                  {index < detail.items.length - 1 && <Divider />}
                </div>
              );
            })
          )}
        </Form.List>
      </Form>
    </div>
  );

  const steps = [
    {
      title: t('warehouse.returnStock'),
      content: formContent,
    },
    {
      title: t('warehouse.returnInfo'),
      content: (
        <Fragment>
          <Typography.Title level={4} style={{ marginTop: -4, marginBottom: isInReturn ? -18 : 0, width: "100%" }} >{orderResult?.number}</Typography.Title>
          {
            isInReturn ? <OutboundOrderForm noModal isReturn sourceId={orderResult?.id ?? ''} onCancel={() => { }} />
              : <OrderDetailV2 noModal data={orderResult || undefined} handleCancel={() => { }} handleRm={() => { }} />
          }
        </Fragment>
      ),
    },
    ...(hasPurchaseFinishStep
      ? [
        {
          title: t('purchase.updatePurchaseOrderStatus'),
          content: (
            <Flex
              vertical
              gap={16}
              style={{ minHeight: "400px", overflow: "auto" }}
            >
              {purchaseItemsNeedFinishUpdate.map(item => {
                const descriptionItems: DescriptionsItemType[] = [
                  {
                    key: `${item.id}-number`,
                    label: t("purchase.orderNumber"),
                    children: item.orderNumber || "-",
                  },
                  {
                    key: `${item.id}-finish-state`,
                    label: t("purchase.purchaseOrderStatus"),
                    children: renderPurchaseFinishStateTag(item.source.orderState),
                  },
                ];

                return (
                  <Descriptions
                    key={item.id}
                    bordered
                    size="small"
                    column={2}
                    items={descriptionItems}
                  />
                );
              })}
              <Flex vertical gap={8} style={{ width: "100%", maxWidth: 420 }}>
                <Typography.Text strong>{t("purchase.purchaseOrderStatus")}</Typography.Text>
                <Select
                  value={selectedOrderState}
                  onChange={value => setSelectedOrderState(value)}
                  placeholder={t('purchase.purchaseOrderStatus')}
                  style={{ width: "100%" }}
                >
                  <Select.Option value={PurchaseOrderStateEnum.PAY_PENDING}>
                    {t("purchase.notPurchase")}
                  </Select.Option>
                  <Select.Option value={PurchaseOrderStateEnum.PAYED}>
                    {t("purchase.purchased")}
                  </Select.Option>
                  {/* <Select.Option value={PurchaseOrderStateEnum.DONE}>
                    {t("locale.purDone")}
                  </Select.Option> */}
                </Select>
              </Flex>
            </Flex>
          ),
        }
      ]
      : []),
    {
      title: t('warehouse.complete'),
      content: (
        <div style={{ minHeight: "400px", display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Result
            icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            title={`${detail.number} ${t('warehouse.returnStockSuccess')}`}
            subTitle={`${t('warehouse.returnNumber')}: ${orderResult?.number ?? ''}`}
          />
        </div>
      ),
    },
  ];

  return (
    <Fragment>
      <Modal
        open={true}
        onOk={handleNext}
        onCancel={handleClose}
        title={
          <Flex gap={6}>
            <span>{t('warehouse.returnStock')}</span>
            <Tag color={orderType?.color}>{opType?.label} {orderType?.label}</Tag>
          </Flex>
        }
        width={1200}
        centered
        okButtonProps={{ loading: isReturning }}
        footer={
          <Flex justify="end" style={{ width: "100%" }}>
            <Space>
              {current === 0 && (
                <Button type="primary" onClick={handleNext}>
                  {t("warehouse.next")}
                </Button>
              )}
              {current === 1 && (
                <Button type="primary" onClick={handleNext}>
                  {t("warehouse.next")}
                </Button>
              )}
              {current === finishStatusStepIndex && (
                <Button type="primary" onClick={handleNext}>
                  {t("warehouse.next")}
                </Button>
              )}
              {current === completeStepIndex && (
                <Button type="primary" onClick={handleNext}>
                  {t("warehouse.confirm")}
                </Button>
              )}
            </Space>
          </Flex>
        }
      >
        <Spin spinning={isReturning}>
          <Steps current={current} items={steps.map(item => ({ title: item.title }))} style={{ marginBottom: 24 }} />
          <Flex vertical style={{ height: '70vh', width: "100%", overflow: "hidden" }}>
            {steps[current].content}
          </Flex>
        </Spin>
      </Modal>

      {/* 确认退库弹窗 */}
      <Modal title={
        <Space>
          <ExclamationCircleTwoTone />
          <span>{t('common.tip')}</span>
        </Space>
      } open={openCon} onOk={handleConfirmModalOk} onCancel={() => setOpenCon(false)}>
        {t('warehouse.confirmReturnTip')}
      </Modal>

      {/* 关闭时是否删除订单弹窗 */}
      <Modal title={
        <Space>
          <ExclamationCircleTwoTone />
          <span>{t('common.tip')}</span>
        </Space>
      } closable={false} maskClosable={false} open={openDel} onOk={handleDelClose} onCancel={() => { setOpenDel(false); onCancel() }}
        okText={t('warehouse.delOrderClose')}
        cancelText={t('warehouse.notDelClose')}
        okButtonProps={{ danger: true }}
      >
        {t('warehouse.delReturnClose', { number: orderResult?.number })}
      </Modal>

      {/* 巡检弹窗：onOk 后执行退库确认逻辑 */}
      <OrderConfirmInspection
        items={inspectionItems}
        open={openInspection}
        onOk={(data) => {
          setOpenInspection(false);
          confirmOrder(data);
        }}
        onClose={() => setOpenInspection(false)}
      />

      {/* 库位选择器 */}
      <LocationSelect
        open={!!currentEditingItem.itemId || !!changingLocation.itemId}
        value={changingLocation.locationId || undefined}
        onChange={handleLocationSelectChange}
        onCancel={() => {
          setCurrentEditingItem({ itemId: null });
          setChangingLocation({ itemId: null, locationId: null });
        }}
      />

      {/* 条码编辑弹窗 */}
      <BarcodeEditModal
        open={barcodeModalInfo.open}
        initialBarcodes={barcodeModalInfo.barcodes}
        originBarcodes={barcodeModalInfo.originBarcodes}
        createNum={barcodeModalInfo.maxCount}
        onOk={handleBarcodeEdit}
        onCancel={() => {
          setBarcodeModalInfo({ open: false, itemId: null, locationId: null, barcodes: [] });
        }}
      />
    </Fragment>
  );
};

export default ReturnModalForm;
